import React, {
	createContext,
	useContext,
	useImperativeHandle,
	useMemo,
} from 'react';
import {
	renderChildrenOrChildrenFunction,
	type IContextChildren,
	type IContextRef,
} from '@netx/core-next';
export {SharedResourceMode} from '@netx/core-share/constants';
import {useSavedCarts, type UseSavedCartsParams} from '../hooks';

export type SavedCartsContextType = ReturnType<typeof useSavedCarts>;

export const SavedCartsContext = createContext<
	SavedCartsContextType | undefined
>(undefined);

export const useSavedCartsProvider = () => {
	const context = useContext(SavedCartsContext);
	if (context === undefined) {
		throw new Error(
			'`useSavedCartsProvider` must be used within a `SavedCartsProvider`',
		);
	}
	return context;
};

export type SavedCartsProviderRef = IContextRef<SavedCartsContextType>;

export type SavedCartsProviderProps = UseSavedCartsParams & {
	ref?: SavedCartsProviderRef;
	children: IContextChildren<SavedCartsContextType>;
};

const SavedCartsProviderInner = (
	{children, ...props}: SavedCartsProviderProps,
	ref: SavedCartsProviderRef,
) => {
	const savedCarts = useSavedCarts(props);

	const contextValues = useMemo(() => savedCarts, [savedCarts]);

	useImperativeHandle(ref, () => contextValues, [contextValues]);

	return (
		<SavedCartsContext.Provider value={contextValues}>
			{renderChildrenOrChildrenFunction(children, contextValues)}
		</SavedCartsContext.Provider>
	);
};

export const SavedCartsProviderPrimitive = React.forwardRef<
	SavedCartsContextType,
	SavedCartsProviderProps
>(SavedCartsProviderInner);

/**
 * SavedCarts context provider
 * @example
 * <SavedCartsProvider ref={savedCartsRef}>
 * 	...
 * </SavedCartsProvider>
 * @example
 * <SavedCartsProvider ref={savedCartsRef}>
 * 	{(savedCarts) => (
 * 		...
 * 	)}
 * </SavedCartsProvider>
 */
export const SavedCartsProvider = React.forwardRef(
	(props: SavedCartsProviderProps, ref: SavedCartsProviderRef) => {
		return <SavedCartsProviderPrimitive ref={ref} {...props} />;
	},
);

export default SavedCartsProvider;
